// src/services/decisionLog.ts
// ---------------------------
// Pure helpers for the Stage 1 DECISION LOG (db-207, table request_decisions).
// At Confirm & Send, ApprovalDecisions works out which items were decided in this
// confirm and sends one row per item to record_request_decisions(). Nothing here
// touches the database — see src/data/requestDecisions.ts for the write.
//
// WHICH items get a row (any one of):
//   (a) NEWLY CONFIRMED — the item is decided (Approved / Declined / Needs Info) and
//       has no confirmedAt yet. applyItemDecision clears confirmedAt whenever the
//       status changes, so this is exactly the set Confirm & Send is about to stamp
//       (the same rule as the "New" badge). Needs NO baseline, so it still works if
//       the session-start snapshot is unavailable.
//   (b) STATUS CHANGED vs the session-start baseline — catches a move back to
//       Pending, which (a) can't see (Pending is never stamped).
//   (c) REASON CHANGED vs the baseline while Declined / Needs Info — a re-worded
//       reason (or reason type) on an already-confirmed decision.
// from_status comes from the baseline (null when unknown).
//
// Must be called with the children BEFORE stampConfirmedAt runs.
// Plan: Claude Doc "Portal Automation Plan" → "Stage 1: record every decision".

import { APPROVAL_STATUSES } from "../utils/enums";
import { calcUniItems, feeTypeLabel } from "../utils/formConstants";

type Category = "fee" | "uniforms" | "shoes" | "device";

const VALID_STATUSES: readonly string[] = [
  APPROVAL_STATUSES.PENDING,
  APPROVAL_STATUSES.APPROVED,
  APPROVAL_STATUSES.DECLINED,
  APPROVAL_STATUSES.NEEDS_INFO,
];

/** The bits of an item the log cares about (all child item shapes have these). */
interface LoggableItem {
  approvalStatus?: string | null;
  confirmedAt?: string | null;
  reason?: string | null;
  reasonCategory?: string | null;
  [key: string]: unknown;
}

interface LoggableChild {
  fees?: LoggableItem[] | null;
  uniforms?: LoggableItem | null;
  shoes?: LoggableItem | null;
  laptop?: LoggableItem | null;
}

/** One item's state at session start. */
export interface ItemBaseline {
  status: string;
  reason: string | null;
  reasonCategory: string | null;
}

interface ChildBaseline {
  fees: ItemBaseline[];
  uniforms: ItemBaseline | null;
  shoes: ItemBaseline | null;
  laptop: ItemBaseline | null;
}

export type DecisionBaseline = Record<number, ChildBaseline>;

/** One row for record_request_decisions (keys match the RPC's p_rows contract). */
export interface DecisionRow {
  child_index: number;
  category: Category;
  fee_index: number | null;
  item_label: string;
  from_status: string | null;
  to_status: string;
  reason: string | null;
  reason_category: string | null;
  item_amount: number | null;
}

function statusOf(item: LoggableItem): string {
  return item.approvalStatus || APPROVAL_STATUSES.PENDING;
}

function textOrNull(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function baselineOf(item: LoggableItem | null | undefined): ItemBaseline | null {
  if (!item) return null;
  return {
    status: statusOf(item),
    reason: textOrNull(item.reason),
    reasonCategory: textOrNull(item.reasonCategory),
  };
}

/** Snapshot every item's status + reason. Take it when the request opens and again
 *  after each successful confirm (alongside sessionStartApprovalsRef). */
export function snapshotDecisionBaseline(
  children: LoggableChild[] | null | undefined
): DecisionBaseline {
  const out: DecisionBaseline = {};
  (children || []).forEach(function (child, ci) {
    out[ci] = {
      fees: (child.fees || []).map(function (f) {
        return baselineOf(f) as ItemBaseline;
      }),
      uniforms: baselineOf(child.uniforms),
      shoes: baselineOf(child.shoes),
      laptop: baselineOf(child.laptop),
    };
  });
  return out;
}

function toAmount(n: number): number | null {
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function num(v: unknown): number {
  return parseFloat(String(v ?? "")) || 0;
}

function amountOf(category: Category, item: LoggableItem): number | null {
  if (category === "fee") {
    const raw = item.feeAmount;
    if (raw == null || String(raw).trim() === "") return null;
    return toAmount(parseFloat(String(raw)));
  }
  if (category === "uniforms") {
    return toAmount(calcUniItems([item as never]));
  }
  if (category === "shoes") {
    return toAmount(num(item.jQty) * num(item.jCost) + num(item.lQty) * num(item.lCost));
  }
  const raw = item.estimatedCost;
  if (raw == null || String(raw).trim() === "") return null;
  return toAmount(parseFloat(String(raw)));
}

function labelOf(category: Category, item: LoggableItem): string {
  if (category === "fee") {
    const ft = textOrNull(item.feeType);
    return ft ? feeTypeLabel(ft) : "School Costs";
  }
  if (category === "uniforms") return "Uniforms";
  if (category === "shoes") return "Shoes";
  return "Device";
}

function rowFor(
  ci: number,
  category: Category,
  feeIndex: number | null,
  item: LoggableItem | null | undefined,
  base: ItemBaseline | null | undefined
): DecisionRow | null {
  if (!item) return null;
  const to = statusOf(item);
  // Never send a status the DB CHECK would reject — that would fail the whole batch.
  if (!VALID_STATUSES.includes(to)) return null;
  const decided = to !== APPROVAL_STATUSES.PENDING;
  const keepsReason = to === APPROVAL_STATUSES.DECLINED || to === APPROVAL_STATUSES.NEEDS_INFO;
  const reason = keepsReason ? textOrNull(item.reason) : null;
  const reasonCategory = keepsReason ? textOrNull(item.reasonCategory) : null;

  const newlyConfirmed = decided && !item.confirmedAt;
  const statusChanged = !!base && base.status !== to;
  const reasonChanged =
    !!base && keepsReason && (base.reason !== reason || base.reasonCategory !== reasonCategory);
  if (!newlyConfirmed && !statusChanged && !reasonChanged) return null;

  const from = base && VALID_STATUSES.includes(base.status) ? base.status : null;
  return {
    child_index: ci,
    category,
    fee_index: category === "fee" ? feeIndex : null,
    item_label: labelOf(category, item),
    from_status: from,
    to_status: to,
    reason,
    reason_category: reasonCategory,
    item_amount: amountOf(category, item),
  };
}

/** The rows to log for this Confirm & Send. Pass children BEFORE stampConfirmedAt.
 *  `baseline` may be null (then only rule (a) applies and from_status is null). */
export function buildDecisionRows(
  baseline: DecisionBaseline | null | undefined,
  children: LoggableChild[] | null | undefined
): DecisionRow[] {
  const rows: DecisionRow[] = [];
  (children || []).forEach(function (child, ci) {
    const b = baseline?.[ci];
    (child.fees || []).forEach(function (f, fi) {
      const r = rowFor(ci, "fee", fi, f, b?.fees?.[fi]);
      if (r) rows.push(r);
    });
    const u = rowFor(ci, "uniforms", null, child.uniforms, b?.uniforms);
    if (u) rows.push(u);
    const s = rowFor(ci, "shoes", null, child.shoes, b?.shoes);
    if (s) rows.push(s);
    const d = rowFor(ci, "device", null, child.laptop, b?.laptop);
    if (d) rows.push(d);
  });
  return rows;
}
